import { ZfBox } from '@zafire/react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import moment from 'moment/moment';
import type { FunctionComponent } from 'react';
import { useMemo, useRef } from 'react';
import { useClient, useGeneralProjectionSettings, useProjection } from '../../../api';
import { DEFAULT_TIME_RANGE } from '../../../config/constants';
import { useChartBaseCustomizationCallbacks } from '../../../hooks/chart/useChartBaseCustomizationCallbacks';
import { useChartFormatter } from '../../../hooks/chart/useChartFormatter';
import { useClientOutputChartCallbacks } from '../../../hooks/chart/useClientOutputChartCallbacks';
import { useMonteCarloSeries } from '../../../hooks/chart/useMonteCarloSeries';
import { useTotalSelectedBankableAssets } from './hooks';
import { getMonteCarloProjectionOptions } from './monteCarloChartOptions';

const MONTE_CARLO_CHART_TYPE = 'MONTE_CARLO_PROJECTION';
const MONTE_CARLO_MAX_TIME_RANGE_OUTPUT = 30;

const calculateProjectionPeriod = (projectionPeriodFromSetting: number) => {
    if (projectionPeriodFromSetting > MONTE_CARLO_MAX_TIME_RANGE_OUTPUT) {
        return MONTE_CARLO_MAX_TIME_RANGE_OUTPUT;
    } else {
        return projectionPeriodFromSetting;
    }
};

export const ClientPackageMonteCarloProjection: FunctionComponent = () => {
    const { data: simulationSettings } = useGeneralProjectionSettings();
    const projectionPeriodYears = calculateProjectionPeriod(simulationSettings?.projectionPeriodYears || DEFAULT_TIME_RANGE);

    const { isSuccess } = useProjection(projectionPeriodYears);

    const { data: client } = useClient();
    const totalSelectedBankableAssets = useTotalSelectedBankableAssets();

    const seriesData = useMonteCarloSeries(true);
    const xAxisYPosition = useRef(undefined);

    const { addAtProjectionStartTextForClientOutput, addAtProjectionEndTextForClientOutput, addCircleMarkerAtGraphStart } = useClientOutputChartCallbacks({
        totalSelectedBankableAssets,
        projectionPeriodYears,
    });

    const customizeChartBase = useChartBaseCustomizationCallbacks({
        clientBirthdate: client!.birthdate,
        isClientOutput: true,
        isProjection: true,
    });

    const { xAxisLabelsFormatter, clientOutputLegendFormatter, yAxisLabelsFormatter } = useChartFormatter({
        chartType: MONTE_CARLO_CHART_TYPE,
        clientBirthdate: client!.birthdate,
        hasWarning: totalSelectedBankableAssets <= 0,
        series: seriesData,
        isClientOutput: true,
        isProjectionOutput: true,
    });

    const highchartsReact = useMemo(() => {
        if (!isSuccess) return null;
        const options = getMonteCarloProjectionOptions({
            clientBirthdate: client!.birthdate,
            legendFormatterFn: clientOutputLegendFormatter,
            onChartRenderEventHandlers: [
                customizeChartBase,
                addCircleMarkerAtGraphStart,
                addAtProjectionStartTextForClientOutput,
                addAtProjectionEndTextForClientOutput,
            ],
            // @ts-expect-error Problems with missing Highcharts types
            refs: { xAxisYPosition },
            seriesData,
            xAxisLabelsFormatterFn: xAxisLabelsFormatter,
            yAxisLabelsFormatterFn: yAxisLabelsFormatter,
            legendFontSize: '2rem',
            xAxisFontSize: '2rem',
            projectionPeriodYears,
            yearRange: {
                from: moment().year(),
                to: moment().year() + projectionPeriodYears,
            },
            isClientOutput: true,
        });

        return (
            <HighchartsReact
                containerProps={{ className: 'chart-container__chart chart-container__client-package' }}
                id={'container'}
                highcharts={Highcharts}
                constructorType="chart"
                options={options}
            />
        );
    }, [
        seriesData,
        client,
        clientOutputLegendFormatter,
        xAxisLabelsFormatter,
        customizeChartBase,
        addAtProjectionStartTextForClientOutput,
        addAtProjectionEndTextForClientOutput,
        addCircleMarkerAtGraphStart,
        projectionPeriodYears,
        yAxisLabelsFormatter,
        isSuccess,
    ]);

    return (
        <ZfBox style={{ position: 'relative', display: 'flex', width: '100%', height: '100%' }}>
            <ZfBox style={{ flexGrow: 1 }}>{highchartsReact}</ZfBox>
        </ZfBox>
    );
};
